import React from 'react';
import { StyleSheet, View } from 'react-native';

import {
  FormSectionTitle,
  MultiStepFormWizard,
  MultiStepFormWizardStep,
  useAppForm,
  useTypedAppFormContext,
} from '@components/Form';
import CreatePlanScreenHeader from '@components/Plan/CreateScreenHeader';
import ScreenContainer from '@components/ScreenContainer';
import { useGroups } from '@context/GroupsContext';
import { useRecommendations } from '@context/RecommendationsContext';
import { useCurrentUser } from '@context/UsersContext';
import { validatePlace } from '@db/Places';
import type { CreatePlanMutationVariables } from '@db/Plans/Queries';
import { useCreatePlanMutation } from '@db/Plans/Queries';
import { errorAlert } from '@helpers/Alert';
import { validateDateRange } from '@helpers/Dates';
import { resetToHomeWithReload } from '@navigation/Actions';
import { CreatePlanScreenProps } from '@navigation/Types';
import { DateRange, Group, Person, Photo, Place, User } from '@types';

export type CreatePlanFormValues = {
  dateRange: DateRange;
  groups: Group[];
  joinsRequested: boolean;
  location: Place | undefined;
  notes: string;
  people: Person[];
  photos: Photo[];
  selectedGroupIds: Set<string>;
  selectedPeopleIds: Set<string>;
};

const DETAILS_HEADER_SOURCE = require('../../assets/images/add-plan.png');
const PEOPLE_HEADER_SOURCE = require('../../assets/images/add-people.png');
const GROUPS_HEADER_SOURCE = require('../../assets/images/add-group.png');
const OPTIONS_HEADER_SOURCE = require('../../assets/images/add-photos.png');
const CREATE_PLAN_KEYBOARD_BOTTOM_OFFSET = 140;

export function getCreatePlanDefaultValues(): CreatePlanFormValues {
  return {
    dateRange: {
      startDate: undefined,
      endDate: undefined,
    },
    groups: [],
    joinsRequested: false,
    location: undefined,
    notes: '',
    people: [],
    photos: [],
    selectedGroupIds: new Set<string>(),
    selectedPeopleIds: new Set<string>(),
  };
}

export function validateCreatePlanForm({ value }: { value: CreatePlanFormValues }) {
  const fields: Partial<Record<keyof CreatePlanFormValues, string>> = {};
  const locationError = validatePlace(value.location);
  const dateRangeError = validateDateRange(value.dateRange);

  if (locationError) {
    fields.location = locationError;
  }
  if (dateRangeError) {
    fields.dateRange = dateRangeError;
  }

  return Object.keys(fields).length > 0 ? { fields } : undefined;
}

export function useCreatePlanForm() {
  return useTypedAppFormContext({
    defaultValues: getCreatePlanDefaultValues(),
  });
}

type CreatePlanScreenContentProps = Pick<CreatePlanScreenProps, 'navigation'>;

type SubmitCreatePlanFormOptions = {
  createPlan: (variables: CreatePlanMutationVariables) => Promise<unknown>;
  navigation: Parameters<typeof resetToHomeWithReload>[0];
  userDoc: Pick<User, 'public'>;
  value: CreatePlanFormValues;
};

export async function submitCreatePlanForm({
  createPlan,
  navigation,
  userDoc,
  value,
}: SubmitCreatePlanFormOptions) {
  const location = value.location;
  const { startDate, endDate } = value.dateRange;
  if (!location || !startDate || !endDate) {
    throw new Error('Create plan form submitted without required details.');
  }

  try {
    await createPlan({
      plan: {
        location,
        description: '',
        startDate,
        endDate,
        notes: value.notes,
        photos: value.photos,
        joinsRequested: value.joinsRequested,
        public: userDoc.public,
      },
      groupIds: [...value.selectedGroupIds],
      people: value.people.filter((person) => value.selectedPeopleIds.has(person.personId)),
    });
    resetToHomeWithReload(navigation);
  } catch (error) {
    errorAlert(error);
    throw error;
  }
}

export default function CreatePlanScreen({ navigation }: CreatePlanScreenProps) {
  const { userDoc } = useCurrentUser();
  const createPlanMutation = useCreatePlanMutation();
  const form = useAppForm({
    defaultValues: getCreatePlanDefaultValues(),
    validators: {
      onSubmit: validateCreatePlanForm,
    },
    onSubmit: ({ value }) => {
      if (!userDoc) {
        throw new Error('Create plan submitted before the current user loaded.');
      }

      return submitCreatePlanForm({
        createPlan: (variables) => createPlanMutation.mutateAsyncOrThrow(variables),
        navigation,
        userDoc,
        value,
      });
    },
  });

  if (!userDoc) {
    return <ScreenContainer loading />;
  }

  return (
    <form.AppForm>
      <CreatePlanScreenContent navigation={navigation} />
    </form.AppForm>
  );
}

export function CreatePlanScreenContent({ navigation }: CreatePlanScreenContentProps) {
  const { userGroups } = useGroups();
  const { recommendedInvitePeople } = useRecommendations();
  const form = useCreatePlanForm();

  React.useEffect(() => {
    form.setFieldValue('people', recommendedInvitePeople);
  }, [form, recommendedInvitePeople]);

  React.useEffect(() => {
    form.setFieldValue('groups', userGroups);
    form.setFieldValue('selectedGroupIds', new Set(userGroups.map((group) => group.groupId)));
  }, [form, userGroups]);

  const renderHeaderComponent = () => (
    <CreatePlanScreenHeader text="Where are you going?" source={DETAILS_HEADER_SOURCE} />
  );

  const renderLocationComponent = () => (
    <View style={styles.inputContainer}>
      <FormSectionTitle icon="map-marker-outline" title="Location" />
      <form.AppField
        name="location"
        validators={{
          onChange: ({ value }) => validatePlace(value) || undefined,
          onSubmit: ({ value }) => validatePlace(value) || undefined,
        }}>
        {(field) => (
          <field.PlaceAutocomplete
            types="(cities)"
            placeholder="Enter a destination"
            testID="components.PlanForm.LocationInput"
          />
        )}
      </form.AppField>
    </View>
  );

  const renderDateComponent = () => (
    <View style={styles.inputContainer}>
      <FormSectionTitle icon="calendar-outline" title="Dates" />
      <form.AppField
        name="dateRange"
        validators={{
          onChange: ({ value }) => validateDateRange(value) || undefined,
          onSubmit: ({ value }) => validateDateRange(value) || undefined,
        }}>
        {(field) => <field.DateRangeInput testID="components.PlanForm.DateInput" />}
      </form.AppField>
    </View>
  );

  const renderDetailsStep = () => (
    <>
      {renderHeaderComponent()}
      {renderLocationComponent()}
      {renderDateComponent()}
    </>
  );

  const renderPeopleStep = () => (
    <>
      <CreatePlanScreenHeader text="Who's going?" source={PEOPLE_HEADER_SOURCE} />
      <View>
        <View style={styles.inputContainer}>
          <FormSectionTitle title="People" icon="account" />
        </View>
        <form.AppField name="selectedPeopleIds">
          {(field) => (
            <form.Subscribe selector={(state) => state.values.joinsRequested}>
              {(joinsRequested) => (
                <field.PersonPickerInput
                  people={recommendedInvitePeople}
                  joinsRequested={joinsRequested}
                  onJoinsRequestedChange={(nextJoinsRequested) =>
                    form.setFieldValue('joinsRequested', nextJoinsRequested)
                  }
                  style={styles.inputContainer}
                  testID="components.PlanForm.PeopleInput"
                />
              )}
            </form.Subscribe>
          )}
        </form.AppField>
      </View>
    </>
  );

  const renderGroupsStep = () => (
    <>
      <CreatePlanScreenHeader text="Who should know?" source={GROUPS_HEADER_SOURCE} />
      <View>
        <View style={styles.groupInputContainer}>
          <FormSectionTitle title="Groups" icon="account-multiple" />
        </View>
        <form.AppField name="selectedGroupIds">
          {(field) => (
            <field.GroupPickerInput
              groups={userGroups}
              style={styles.groupInputContainer}
              testID="components.PlanForm.GroupInput"
            />
          )}
        </form.AppField>
      </View>
    </>
  );

  const renderOptionsStep = () => (
    <>
      <CreatePlanScreenHeader text="Anything to add?" source={OPTIONS_HEADER_SOURCE} />
      <View style={styles.inputContainer}>
        <FormSectionTitle title="Notes" icon="text" />
        <form.AppField name="notes">
          {(field) => (
            <field.TextInput
              placeholder="Add details"
              multiline
              numberOfLines={4}
              style={styles.notesInput}
              testID="components.PlanForm.NotesInput"
            />
          )}
        </form.AppField>
      </View>
      <View>
        <View style={styles.inputContainer}>
          <FormSectionTitle title="Photos" icon="image-outline" />
        </View>
        <form.AppField name="photos">
          {(field) => <field.PhotoListInput testID="components.PlanForm.PhotoInput" />}
        </form.AppField>
      </View>
    </>
  );

  const steps: MultiStepFormWizardStep<CreatePlanFormValues, typeof form>[] = [
    {
      id: 'details',
      fields: {
        location: 'location',
        dateRange: 'dateRange',
      },
      nextLabel: 'Next',
      primaryButtonTestID: 'screens.CreatePlanForm.CreatePlanFormDetailsScreen.Button.Next',
      render: renderDetailsStep,
    },
    {
      id: 'people',
      fields: {
        selectedPeopleIds: 'selectedPeopleIds',
        joinsRequested: 'joinsRequested',
        people: 'people',
      },
      nextLabel: 'Next',
      primaryButtonTestID: 'screens.CreatePlanForm.CreatePlanFormPeopleScreen.Button.Next',
      render: renderPeopleStep,
    },
    {
      id: 'groups',
      fields: {
        selectedGroupIds: 'selectedGroupIds',
        groups: 'groups',
      },
      nextLabel: 'Next',
      primaryButtonTestID: 'screens.CreatePlanForm.CreatePlanFormGroupsScreen.Button.Next',
      render: renderGroupsStep,
    },
    {
      id: 'options',
      fields: {
        notes: 'notes',
        photos: 'photos',
      },
      render: renderOptionsStep,
    },
  ];

  return (
    <ScreenContainer
      scrollable
      keyboardBottomOffset={CREATE_PLAN_KEYBOARD_BOTTOM_OFFSET}
      testID="screens.CreatePlanForm.CreatePlanFormDetailsScreen.Background">
      <MultiStepFormWizard
        form={form}
        steps={steps}
        submitLabel="Post"
        submitButtonTestID="screens.CreatePlanForm.CreatePlanFormOptionsScreen.SingleTapButton.Post"
      />
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  groupInputContainer: {
    paddingHorizontal: 20,
  },
  inputContainer: {
    marginHorizontal: 20,
  },
  notesInput: {
    height: 100,
    marginVertical: 5,
  },
});
